import { Box, GridProps, Popover, PopoverProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NewContractIcon from 'components/SVG/NewContractIcon';
import React, { CSSProperties } from 'react';
import IconButtonWrapper from './IconButtonWrapper';
import NewExpandIcon from 'components/SVG/NewExpandIcon';
import { useDragHelpers } from 'hooks/common/useDragHelpers';
import { motion } from 'framer-motion';
import { useBoardNodesContext, useResizeHelpers } from 'hooks/common';
import ResizableContainerBorderRendered from './ResizableContainerBorderRendered';
import { UNIQUE_SELECTOR_ID } from 'constants/index';
import SparkScrollableGridContainer from './SparkScrollableGridContainer';

type RenderFunctionProps = {
  isExpanded: boolean;
};

type SparkOverlayProps = {
  onClose: () => void;
  open: boolean;
  childrenContainerStyles?: GridProps['sx'];
  popoverSx?: PopoverProps['sx'];
  actions?: React.ReactNode;
};

type SparkOverLayPropsWithChildren = {
  children: React.ReactNode;
  render?: never;
};
type SparkOverlayPropsWithRenderProp = {
  render: (props: RenderFunctionProps) => React.ReactNode;
  children?: never;
};

type Props = (SparkOverLayPropsWithChildren | SparkOverlayPropsWithRenderProp) &
  SparkOverlayProps;

function SparkOverlay({
  children,
  onClose,
  open,
  childrenContainerStyles = {},
  popoverSx = {},
  actions,
  render,
}: Props) {
  const id = `${UNIQUE_SELECTOR_ID.SPARK_OVERLAY}-${Date.now}`;
  const [isFullScreen, setIsFullScreen] = React.useState<boolean>(true);

  const {
    handleMouseDownDrag,
    position,
    draggableContainerStyles,
    isDragging,
  } = useDragHelpers(isFullScreen || !open);
  const { setIsBoardNodeCollapsedView } = useBoardNodesContext();
  const { dimensions, startResize, isResizing, setDimensions } =
    useResizeHelpers(id, isFullScreen || !open);

  const defaultContractedPosition = React.useMemo(() => {
    return {
      top: 0,
      left: window.innerWidth - 500,
      width: 500,
      height: 600,
    };
  }, []);

  const positionStyles: CSSProperties = {
    position: 'absolute',
    borderRadius: isFullScreen ? '0px' : '10px',
    top: isFullScreen ? '0px' : dimensions.top,
    left: isFullScreen ? '0px' : dimensions.left,
    transform: isFullScreen
      ? 'none'
      : `translate(${position.x}px, ${position.y}px)`,
  };

  const dimensionStyles: CSSProperties = {
    width: isFullScreen ? '100%' : dimensions.width,
    height: isFullScreen ? '100vh' : dimensions.height,
    minWidth: '400px',
  };

  return (
    <Popover
      id={id}
      className={UNIQUE_SELECTOR_ID.SPARK_OVERLAY_CLASS}
      onClick={(e) => e.stopPropagation()}
      anchorEl={document.body}
      open={open}
      component={motion.div}
      onMouseDown={handleMouseDownDrag}
      sx={{
        ...positionStyles,
        ...dimensionStyles,
        bgcolor: 'rgba(98, 103, 172,0.9)',
        transition: (theme) =>
          isDragging || isResizing
            ? 'none'
            : theme.transitions.create(
                ['width', 'height', 'top', 'left', 'transform'],
                {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }
              ),
        '& .MuiBackdrop-root': {
          display: 'none',
        },
        ...draggableContainerStyles,
        ...popoverSx,
      }}
      slotProps={{
        paper: {
          sx: {
            bgcolor: 'transparent',
            position: 'relative',
            borderRadius: '10px',
            padding: 0,
            margin: 0,
            width: '100%',
            height: '100%',
            maxWidth: 'unset',
            maxHeight: 'unset',
            top: 'unset !important',
            left: 'unset !important',
            transition: (theme) =>
              theme.transitions.create(['width', 'height'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
          },
        },
      }}
    >
      <SparkScrollableGridContainer
        actions={actions}
        actionContainerStyles={{
          display: 'flex',
          justifyContent: 'center',
        }}
        headerStyle={{ borderBottom: 'none' }}
        header={
          <Box
            width='100%'
            display='flex'
            justifyContent='flex-end'
            position='sticky'
            top={0}
            zIndex={200}
            height='3.5rem'
          >
            {isFullScreen ? (
              <IconButtonWrapper
                onClick={() => {
                  setDimensions(defaultContractedPosition);
                  setIsFullScreen(false);
                  setIsBoardNodeCollapsedView(true);
                }}
                sx={{ width: '40px', height: '40px' }}
              >
                <NewContractIcon />
              </IconButtonWrapper>
            ) : (
              <IconButtonWrapper
                onClick={() => {
                  setIsFullScreen(true);
                  setIsBoardNodeCollapsedView(false);
                }}
                sx={{ width: '40px', height: '40px' }}
              >
                <NewExpandIcon />
              </IconButtonWrapper>
            )}
            <IconButtonWrapper
              onClick={() => {
                onClose();
                setIsBoardNodeCollapsedView(false);
              }}
              sx={{ width: '40px', height: '40px' }}
            >
              <CloseIcon
                sx={{ color: (theme) => theme.palette.common.black }}
              />
            </IconButtonWrapper>
          </Box>
        }
        scrollableContainerStyle={childrenContainerStyles}
      >
        {render ? render({ isExpanded: isFullScreen }) : children}
      </SparkScrollableGridContainer>

      <ResizableContainerBorderRendered startResize={startResize} />
    </Popover>
  );
}

export default SparkOverlay;
